import path from 'path';
import {SandboxInstance} from '@blaxel/core';
import {
  CODE_MODULE_ARTIFACTS,
  CodeModuleArtifact,
} from '@packages/systems/code-modules/core';
import {Result} from '@packages/systems/wfdl';
import {z} from 'zod/v4';
import semver from 'semver';
import {
  SANDBOX_WORKING_DIR,
  WEBFLOW_DEPENDENCIES,
  DEFAULT_DEPENDENCIES,
} from '../constants';
import {
  isPinnedVersion,
  projectDependenciesFromPackageJson,
} from '../projectors/dependencies';
import {scanUserImports} from './scanImports';
import {execDurationMs} from './serverTiming';
import {isValidNpmPackageName} from './isValidNpmPackageName';
import type {PublishBundleStep} from './classifyPublishBundleFailure';

export const Build = z.object({
  js: z.string().describe('The bundled JS'),
  css: z.string().describe('The bundled CSS'),
  functionsHash: z
    .string()
    .optional()
    .describe(
      'Hash from dist-functions/index.[hash].js when codeFunctions ran and the file exists'
    ),
});

export type BuildPhase =
  | 'deps_install'
  | 'typecheck'
  | 'vite_build'
  | 'fs_read';

/**
 * Per-phase wall-clock timing (ms) and failure attribution for a buildCodeLibrary
 * invocation. A phase duration is present iff that phase executed; earlier
 * failures short-circuit subsequent phases.
 *
 * `phaseDurations` is measured on the client (wraps `await sandbox.process.exec`)
 * and therefore inflates whenever the tab is backgrounded mid-build — the
 * browser throttles the event loop, delaying Promise resolution.
 *
 * `serverPhaseDurations` is derived from `startedAt`/`completedAt` on the
 * Blaxel `ProcessResponse`, so the values come from the sandbox clock and are
 * immune to client throttling. Phases driven by `sandbox.fs.*` (e.g. `fs_read`)
 * have no process response and are absent from this map.
 *
 * `depsInstall` carries APAI-3268 self-heal observability so the engine can
 * emit per-name `vccBadPackageName` events for imports the build pipeline
 * dropped (rejected pre-install) or recovered (retried after `bun add` 404).
 * The `cacheRepair*` flags carry EXT-596 self-heal observability for transient
 * sandbox corruption (EEXIST relink / FileNotFound copy-from-cache) that the
 * repair-and-retry path cleared. Empty / `false` on the happy path.
 */
export type BuildMeta = {
  phaseDurations: Partial<Record<BuildPhase, number>>;
  serverPhaseDurations: Partial<Record<BuildPhase, number>>;
  phaseLogs: Partial<Record<BuildPhase, string>>;
  failurePhase: BuildPhase | 'none';
  depsInstall: {
    rejectedNames: string[];
    recoveredNames: string[];
    retryHappened: boolean;
    retryRecovered: boolean;
    cacheRepairHappened: boolean;
    cacheRepairRecovered: boolean;
  };
};

const emptyDepsInstallMeta: BuildMeta['depsInstall'] = {
  rejectedNames: [],
  recoveredNames: [],
  retryHappened: false,
  retryRecovered: false,
  cacheRepairHappened: false,
  cacheRepairRecovered: false,
};

export const BundleFile = z.object({
  artifact: z
    .enum(CodeModuleArtifact)
    .describe('The artifact lane the file belongs to'),
  name: z.string().describe('The name of the file'),
  content: z
    .union([
      z.string(),
      z.custom<Buffer>(
        (value) => typeof Buffer !== 'undefined' && Buffer.isBuffer(value)
      ),
    ])
    .describe('The content of the file'),
});

export type Build = z.infer<typeof Build>;
export type BundleFile = z.infer<typeof BundleFile>;

/**
 * @deprecated Prefer `isValidNpmPackageName`. Kept as an export so external
 * callers (and tests) that still consume the raw regex don't break — but
 * the build pipeline now filters via the stricter validator.
 */
export const NPM_PACKAGE_RE =
  /^(@[a-z0-9-~][a-z0-9-._~]*\/)?[a-z0-9-~][a-z0-9-._~]*$/;
const LOG_PATH_REGEX = /:\s+(\/[^\s]+-debug\.log)$/m;
// Major upgrades can change the sandbox command contract, so they require an
// explicit rollout.
const LATEST_STABLE_MAJOR = 2;
const WEBFLOW_DEPENDENCIES_SET = new Set(WEBFLOW_DEPENDENCIES);
const DEFAULT_DEPENDENCIES_SET = new Set(DEFAULT_DEPENDENCIES);
const DIAGNOSTICS_MAX_LENGTH = 9000;
const DIAGNOSTICS_TAIL_LENGTH = 1000;

function isEligibleForStableMajorUpdate(
  specifier: string | undefined
): boolean {
  // Absent from package.json must not be `bun update`'d in.
  if (specifier === undefined) return false;
  if (isPinnedVersion(specifier)) return false;
  return (semver.coerce(specifier)?.major ?? Infinity) <= LATEST_STABLE_MAJOR;
}

async function webflowDependenciesForStableMajorUpdate(
  sandbox: SandboxInstance
): Promise<string[]> {
  try {
    const source = await sandbox.fs.read(`${SANDBOX_WORKING_DIR}/package.json`);
    const deps = projectDependenciesFromPackageJson(source) ?? {};
    return WEBFLOW_DEPENDENCIES.filter((name) =>
      isEligibleForStableMajorUpdate(deps[name]?.version)
    );
  } catch {
    return [];
  }
}

// Matches the two shapes bun emits for a missing-on-registry package:
//   error: GET https://registry.npmjs.org/<urlencoded-name> - 404
//   error: <name>@<ver> failed to resolve
// Used by the retry path to identify which specific packages failed so we
// can drop them and retry without aborting the whole build.
//
// Both regexes anchor to start-of-line (`^` + `m` flag) so an embedded
// substring inside an unrelated error message can't trigger a false
// positive that drops user-imported packages we shouldn't drop.
const BUN_404_URL_RE =
  /^error:\s+GET\s+https:\/\/registry\.npmjs\.org\/([^\s]+?)(?:\/[^\s]*)?\s+-\s+404/gm;
const BUN_FAILED_TO_RESOLVE_RE =
  /^error:\s+(@?[^@\s]+?)(?:@[^\s]+)?\s+failed to resolve/gm;
const BUN_TRANSIENT_INSTALL_CORRUPTION_RE =
  /^(?:error:\s+Failed to link .+:\s+EEXIST|FileNotFound:\s+failed copying files from cache to destination for package .+)$/m;

/**
 * Filters `userImports` to keep only entries that are syntactically valid
 * npm package specifiers and aren't already provided by the Webflow set
 * (those are installed separately so a duplicate `bun add` would just be
 * noise — and the user's code shouldn't override the platform packages).
 *
 * Returns both the kept and rejected lists so the caller can pipe rejected
 * names to telemetry (`vccBadPackageName`). Rejected names are surfaced
 * back to the AIA via the tool-response layer so the LLM can self-correct
 * imports it hallucinated.
 */
export function resolveDependencies(
  knownDeps: string[],
  userImports: string[]
): {deps: string[]; rejected: string[]} {
  const rejected: string[] = [];
  const validUserDeps: string[] = [];
  for (const pkg of userImports) {
    if (WEBFLOW_DEPENDENCIES_SET.has(pkg)) continue;
    if (isValidNpmPackageName(pkg)) {
      validUserDeps.push(pkg);
    } else {
      rejected.push(pkg);
    }
  }
  return {
    deps: [...new Set([...knownDeps, ...validUserDeps])],
    rejected,
  };
}

/**
 * Extracts package names bun reported as unresolvable from a non-zero
 * `bun add` log. Returns an empty array if bun's output didn't contain any
 * parseable failure lines (transient registry 5xx, network errors,
 * malformed log, etc.) — the caller treats that as "can't safely retry"
 * and falls back to the original failure path.
 */
export function parseFailedPackagesFromBunLogs(logs: string): string[] {
  const failed = new Set<string>();
  for (const match of logs.matchAll(BUN_404_URL_RE)) {
    // Capture group 1 is required by both regexes, but `noUncheckedIndexedAccess`
    // types it as `string | undefined` — guard so the rest of the pipeline
    // stays strict-mode clean.
    const encoded = match[1];
    if (!encoded) continue;
    try {
      // bun URL-encodes scopes (`@scope%2fname`) — decode so we compare
      // against the same identifiers we passed in.
      failed.add(decodeURIComponent(encoded));
    } catch {
      // Skip malformed encodings rather than throw — the retry path is
      // best-effort, not a hard contract.
    }
  }
  for (const match of logs.matchAll(BUN_FAILED_TO_RESOLVE_RE)) {
    const name = match[1];
    if (name) failed.add(name);
  }
  return [...failed];
}

function hasTransientBunInstallCorruption(logs: string): boolean {
  return BUN_TRANSIENT_INSTALL_CORRUPTION_RE.test(logs);
}

/**
 * Sums two server durations, propagating `null` (unavailable). A single
 * unavailable sub-step makes the whole phase's server duration unavailable,
 * since we'd otherwise report a misleadingly-low number.
 */
function addServerDuration(a: number | null, b: number | null): number | null {
  if (a === null || b === null) return null;
  return a + b;
}

type DepsInstallSuccess = {
  logs: string;
  meta: BuildMeta['depsInstall'];
};

/**
 * Installs and updates npm dependencies in the sandbox.
 *
 * Returns the accumulated install/update logs alongside the sandbox-reported
 * wall-clock duration summed across the underlying `process.exec` calls and
 * the APAI-3268 self-heal meta (`depsInstall.{rejected,recovered}Names`,
 * retry flags). `serverDurationMs` is `null` when any sub-step lacks sandbox
 * timestamps so we don't report a misleadingly-low partial total.
 */
export async function installDependencies(
  sandbox: SandboxInstance,
  {updateWebflowDeps = true}: {updateWebflowDeps?: boolean} = {}
): Promise<{
  result: Result.ResultType<string, DepsInstallSuccess>;
  serverDurationMs: number | null;
  meta: BuildMeta['depsInstall'];
}> {
  const depsInstall: BuildMeta['depsInstall'] = {
    rejectedNames: [],
    recoveredNames: [],
    retryHappened: false,
    retryRecovered: false,
    cacheRepairHappened: false,
    cacheRepairRecovered: false,
  };
  const logs: string[] = [];

  const scan = await scanUserImports(sandbox);
  const {packages: userImports, rejected: sandboxRejected} = scan;
  const {deps: allDeps, rejected: clientRejected} = resolveDependencies(
    DEFAULT_DEPENDENCIES,
    userImports
  );
  // Merge sandbox-side rejections (caught by the validator baked into the
  // Blaxel image) with the Designer-side defensive filter. Either layer
  // could short-circuit before the other sees the bad name, so reporting
  // the union keeps the telemetry honest about where the catch happened.
  // Dedup via Set since the two filters apply the same rules and would
  // otherwise double-count anything the sandbox passed through.
  depsInstall.rejectedNames = [
    ...new Set([...sandboxRejected, ...clientRejected]),
  ];

  // Dependency install and dependency update must be sequential to avoid race conditions
  // when bun attempts to create symlinks for shared transitive dependencies, which can lead to
  // EEXIST errors when multiple processes try to create the same symlink.
  const runBunAdd = (packages: string[]) =>
    sandbox.process.exec({
      command: `bun add -E --only-missing ${packages.join(' ')}`,
      workingDir: SANDBOX_WORKING_DIR,
      waitForCompletion: true,
    });

  let depsResult = await runBunAdd(allDeps);
  logs.push(depsResult.logs);
  let serverDurationMs = addServerDuration(
    scan.serverDurationMs,
    execDurationMs(depsResult)
  );

  if (
    depsResult.exitCode !== 0 &&
    hasTransientBunInstallCorruption(depsResult.logs)
  ) {
    // EXT-596: the per-site sandbox is long-lived, so a `bun add` failure
    // carrying the transient-corruption signature has two distinct causes
    // that need different fixes:
    //   - `Failed to link <pkg>: EEXIST` — a partial `node_modules` left by an
    //     interrupted prior install; cleared by removing `node_modules`.
    //   - `FileNotFound: failed copying files from cache to destination` — a
    //     corrupt/missing entry in bun's global install cache (lives outside
    //     SANDBOX_WORKING_DIR, shared across builds in this sandbox). Removing
    //     `node_modules` does NOT fix this — the next install copies from the
    //     same bad cache entry and fails again. `bun pm cache rm` evicts the
    //     corrupt cache so the retry re-downloads and repopulates it cleanly.
    // We clear both sources, then retry the normal `--only-missing` install
    // (no `--force`: resolution stays lockfile-faithful, and the corrupt
    // entries are gone rather than merely bypassed).
    depsInstall.cacheRepairHappened = true;
    const repairResult = await sandbox.process.exec({
      command: `rm -rf ${SANDBOX_WORKING_DIR}/node_modules && bun pm cache rm`,
      workingDir: SANDBOX_WORKING_DIR,
      waitForCompletion: true,
    });
    logs.push(repairResult.logs);
    serverDurationMs = addServerDuration(
      serverDurationMs,
      execDurationMs(repairResult)
    );
    if (repairResult.exitCode !== 0) {
      return {
        result: Result.Error(logs.join('\n')),
        serverDurationMs,
        meta: depsInstall,
      };
    }

    depsResult = await runBunAdd(allDeps);
    logs.push(depsResult.logs);
    serverDurationMs = addServerDuration(
      serverDurationMs,
      execDurationMs(depsResult)
    );
    depsInstall.cacheRepairRecovered = depsResult.exitCode === 0;
  }

  if (depsResult.exitCode !== 0) {
    // Try to identify which specific packages npm rejected. If the failed
    // packages came from user code (not the platform default set), we can
    // drop them and retry — the build will still fail later in vite_build
    // with a clean "unresolved import" message, but the AIA gets that as
    // structured diagnostics it can self-correct from. If none of the
    // failures are user-attributable, retrying would be pointless.
    const failedPackages = parseFailedPackagesFromBunLogs(depsResult.logs);
    const userFailed = failedPackages.filter(
      (pkg) => !DEFAULT_DEPENDENCIES_SET.has(pkg)
    );
    // Bail to the original error if there's nothing safe to drop:
    //  - no parseable failure (transient network / registry 5xx)
    //  - every failure is a default dep (would leave the build broken)
    //  - dropping leaves us with zero installable packages
    const canRetry =
      userFailed.length > 0 && userFailed.length < allDeps.length;
    if (!canRetry) {
      return {
        result: Result.Error(logs.join('\n')),
        serverDurationMs,
        meta: depsInstall,
      };
    }

    const userFailedSet = new Set(userFailed);
    const reducedDeps = allDeps.filter((pkg) => !userFailedSet.has(pkg));
    depsInstall.retryHappened = true;
    depsInstall.recoveredNames = userFailed;
    const retryResult = await runBunAdd(reducedDeps);
    logs.push(retryResult.logs);
    serverDurationMs = addServerDuration(
      serverDurationMs,
      execDurationMs(retryResult)
    );
    if (retryResult.exitCode !== 0) {
      // Surface the original failure rather than the retry's — it has the
      // full context of what was rejected on the first pass.
      return {
        result: Result.Error(logs.join('\n')),
        serverDurationMs,
        meta: depsInstall,
      };
    }
    depsInstall.retryRecovered = true;
    depsInstall.cacheRepairRecovered ||= depsInstall.cacheRepairHappened;
  }

  // Dist-tags and `file:` stay as written. Callers that only need missing
  // user imports present (e.g. the write-time type check in the AIA tools)
  // skip this; it's a registry round-trip that would add latency to every
  // write without affecting whether a newly imported package resolves.
  if (updateWebflowDeps) {
    const webflowDepsToUpdate =
      await webflowDependenciesForStableMajorUpdate(sandbox);
    if (webflowDepsToUpdate.length > 0) {
      const depsUpdateResult = await sandbox.process.exec({
        command: `bun update ${webflowDepsToUpdate
          .map((dependency) => `${dependency}@${LATEST_STABLE_MAJOR}.x.x`)
          .join(' ')}`,
        workingDir: SANDBOX_WORKING_DIR,
        waitForCompletion: true,
      });
      logs.push(depsUpdateResult.logs);
      serverDurationMs = addServerDuration(
        serverDurationMs,
        execDurationMs(depsUpdateResult)
      );
      if (depsUpdateResult.exitCode !== 0) {
        return {
          result: Result.Error(logs.join('\n')),
          serverDurationMs,
          meta: depsInstall,
        };
      }
    }
  }

  return {
    result: Result.Ok({logs: logs.join('\n'), meta: depsInstall}),
    serverDurationMs,
    meta: depsInstall,
  };
}

async function readBundleFiles(
  sandbox: SandboxInstance,
  artifact: CodeModuleArtifact,
  {
    optional = false,
    portable = false,
  }: {optional?: boolean; portable?: boolean} = {}
): Promise<BundleFile[]> {
  try {
    const root = `${SANDBOX_WORKING_DIR}/${CODE_MODULE_ARTIFACTS[artifact].outDir}`;
    if (!portable) {
      const {files} = await sandbox.fs.ls(root);
      return Promise.all(
        files.map(async (file) => ({
          artifact,
          name: file.name,
          content: await sandbox.fs.read(file.path),
        }))
      );
    }
    const relativePath = (filePath: string) => {
      const relative = path.posix.relative(root, filePath);
      if (
        !relative ||
        relative === '..' ||
        relative.startsWith('../') ||
        path.posix.isAbsolute(relative)
      ) {
        throw new Error('Bundle file is outside its artifact directory');
      }
      return relative;
    };
    const result: BundleFile[] = [];
    const pending = [root];
    while (pending.length) {
      const {files, subdirectories} = await sandbox.fs.ls(pending.pop()!);
      for (let offset = 0; offset < files.length; offset += 8) {
        result.push(
          ...(await Promise.all(
            files.slice(offset, offset + 8).map(async (file) => {
              const name = relativePath(file.path);
              const bytes = await sandbox.fs.readBinary(file.path);
              return {
                artifact,
                name,
                content: Buffer.from(await bytes.arrayBuffer()),
              };
            })
          ))
        );
      }
      for (const directory of subdirectories) {
        relativePath(directory.path);
        pending.push(directory.path);
      }
    }
    return result;
  } catch (error) {
    if (optional && error instanceof Error && error.message.includes('404')) {
      return [];
    }
    throw error;
  }
}

/**
 * `extractLogs` only concatenates a CLI debug file. If the binary never
 * started, that file does not exist and `logs` is empty — fall back to the
 * process streams Blaxel still captures.
 */
function logsOrProcessOutput(
  extractedLogs: string,
  result: {stdout?: string; stderr?: string}
): string {
  if (extractedLogs.trim().length > 0) {
    return extractedLogs;
  }
  return [result.stdout, result.stderr].filter(Boolean).join('\n');
}

async function extractLogs(
  sandbox: SandboxInstance,
  logs: string
): Promise<string> {
  const logPathMatch = logs.match(LOG_PATH_REGEX);
  const logFilePath = logPathMatch ? logPathMatch[1] : null;
  if (logFilePath) {
    try {
      const logFileContent = await sandbox.fs.read(logFilePath);
      return logFileContent ? logs + '\n' + logFileContent : logs;
    } catch {
      // best-effort: fall back to the exec logs we already have
    }
  }
  return logs;
}

function truncateDiagnostics(
  diagnostics: string,
  maxLength = DIAGNOSTICS_MAX_LENGTH
): string {
  if (diagnostics.length <= maxLength + DIAGNOSTICS_TAIL_LENGTH) {
    return diagnostics;
  }
  const truncatedText = '\n...truncated...\n';
  const head = diagnostics.slice(0, maxLength - truncatedText.length);
  const tail = diagnostics.slice(diagnostics.length - DIAGNOSTICS_TAIL_LENGTH);
  return `${head}${truncatedText}${tail}`;
}

type PerformTypeCheckOptions = {
  // Designer tools expect `{success: false, diagnostics}` even when exec
  // throws. Publish needs the throw so `runWithRetry` can retry Blaxel 5xx.
  propagateExecErrors?: boolean;
};

async function performTypeCheckWithTiming(
  sandbox: SandboxInstance,
  options: PerformTypeCheckOptions = {}
): Promise<{
  result: {success: true} | {success: false; diagnostics: string};
  serverDurationMs: number | null;
}> {
  try {
    const exec = await sandbox.process.exec({
      command: 'bun run tsc --noEmit --pretty false',
      workingDir: SANDBOX_WORKING_DIR,
      waitForCompletion: true,
    });
    const serverDurationMs = execDurationMs(exec);
    if (exec.exitCode !== 0) {
      return {
        result: {
          success: false,
          diagnostics: truncateDiagnostics(exec.logs),
        },
        serverDurationMs,
      };
    }
    return {result: {success: true}, serverDurationMs};
  } catch (error) {
    if (options.propagateExecErrors) {
      throw error;
    }
    const message = error instanceof Error ? error.message : String(error);
    return {
      result: {
        success: false,
        diagnostics: truncateDiagnostics(message),
      },
      serverDurationMs: null,
    };
  }
}

export async function performTypeCheck(
  sandbox: SandboxInstance,
  options: PerformTypeCheckOptions = {}
): Promise<{success: true} | {success: false; diagnostics: string}> {
  return (await performTypeCheckWithTiming(sandbox, options)).result;
}

/**
 * Times an async phase and records both client wall-clock and (when reported
 * by the phase) sandbox-side duration. Uses try/finally so the client-side
 * duration is captured even when `fn` throws.
 *
 * `fn` returns `{value, serverDurationMs}`. `serverDurationMs` is `null` when
 * the phase has no sandbox-side timing source (e.g. the `fs_read` phase calls
 * `sandbox.fs.*` rather than `sandbox.process.exec` and therefore has no
 * `ProcessResponse` to read timestamps from).
 */
async function timePhase<T>(
  phase: BuildPhase,
  clientDurations: BuildMeta['phaseDurations'],
  serverDurations: BuildMeta['serverPhaseDurations'],
  fn: () => Promise<{value: T; serverDurationMs: number | null}>
): Promise<T> {
  const start = performance.now();
  try {
    const {value, serverDurationMs} = await fn();
    if (serverDurationMs !== null) {
      serverDurations[phase] = serverDurationMs;
    }
    return value;
  } finally {
    clientDurations[phase] = performance.now() - start;
  }
}

/**
 * Bundles a code library using Vite in a sandbox environment.
 *
 * This function performs the following steps:
 * 1. Scans user imports and installs all required dependencies
 * 2. Executes Vite build to bundle the code library into JS and CSS
 * 3. Reads the bundled output files (out.mjs and out.css)
 * 4. Lists installed dependencies for tracking
 *
 *
 * @param sandbox - The sandbox instance where the bundling will be executed
 * @returns `result` holds the bundled JS/CSS or an error message.
 *          `meta` records per-phase durations and the phase that failed (if any)
 *          so callers can emit telemetry for SLO tracking.
 */
export type BuildCodeLibraryOptions = {
  /**
   * When true, the dev-preview `vite build` runs with `WF_STAMP_SOURCE_LOC` set
   * so the source-loc Babel plugin stamps rendered elements with `data-wf-loc-text`
   * for instant text apply. Off by default — no stamps, no behavior change.
   */
  stampSourceLoc?: boolean;
  /**
   * When true, prefer `scripts/build.ts` so the next-image stub plugin
   * replaces function modules. Off by default: `bun run vite build`.
   * `webflow-base` has no wrapper, so the `if` falls through to the same command.
   */
  codeFunctions?: boolean;
};

export function getViteBuildCommand({
  codeFunctions,
}: Pick<BuildCodeLibraryOptions, 'codeFunctions'>): string {
  return codeFunctions
    ? 'if [ -f scripts/build.ts ]; then bun ./scripts/build.ts; else bun run vite build; fi'
    : 'bun run vite build';
}

export async function buildCodeLibrary(
  sandbox: SandboxInstance,
  options: BuildCodeLibraryOptions = {}
): Promise<{
  result: Result.ResultType<string, Build>;
  meta: BuildMeta;
}> {
  const phaseDurations: BuildMeta['phaseDurations'] = {};
  const serverPhaseDurations: BuildMeta['serverPhaseDurations'] = {};
  const phaseLogs: BuildMeta['phaseLogs'] = {};
  let depsInstallMeta: BuildMeta['depsInstall'] = emptyDepsInstallMeta;

  const installResult = await timePhase(
    'deps_install',
    phaseDurations,
    serverPhaseDurations,
    async () => {
      const {result, serverDurationMs, meta} =
        await installDependencies(sandbox);
      depsInstallMeta = meta;
      return {value: result, serverDurationMs};
    }
  );
  if (installResult.tag === 'Error') {
    phaseLogs.deps_install = installResult.val;
    return {
      result: Result.Error(installResult.val),
      meta: {
        phaseDurations,
        serverPhaseDurations,
        phaseLogs,
        failurePhase: 'deps_install',
        depsInstall: depsInstallMeta,
      },
    };
  }
  phaseLogs.deps_install = installResult.val.logs;
  depsInstallMeta = installResult.val.meta;

  const typeCheckResult = await timePhase(
    'typecheck',
    phaseDurations,
    serverPhaseDurations,
    async () => {
      const {result, serverDurationMs} =
        await performTypeCheckWithTiming(sandbox);
      return {value: result, serverDurationMs};
    }
  );
  if ('diagnostics' in typeCheckResult) {
    phaseLogs.typecheck = typeCheckResult.diagnostics;
  }
  if (!typeCheckResult.success) {
    return {
      result: Result.Error(typeCheckResult.diagnostics),
      meta: {
        phaseDurations,
        serverPhaseDurations,
        phaseLogs,
        failurePhase: 'typecheck',
        depsInstall: depsInstallMeta,
      },
    };
  }

  const viteResult = await timePhase(
    'vite_build',
    phaseDurations,
    serverPhaseDurations,
    async () => {
      const exec = await sandbox.process.exec({
        command: getViteBuildCommand(options),
        workingDir: SANDBOX_WORKING_DIR,
        waitForCompletion: true,
        ...(options.stampSourceLoc ? {env: {WF_STAMP_SOURCE_LOC: '1'}} : {}),
      });
      return {value: exec, serverDurationMs: execDurationMs(exec)};
    }
  );
  phaseLogs.vite_build = viteResult.logs;
  // sandbox.process.exec resolves regardless of exit code. Without this
  // explicit check, a vite failure followed by a stale `dist/out.mjs` from
  // a prior build would silently surface as success with broken output.
  if (viteResult.exitCode !== 0) {
    return {
      result: Result.Error(truncateDiagnostics(viteResult.logs)),
      meta: {
        phaseDurations,
        serverPhaseDurations,
        phaseLogs,
        failurePhase: 'vite_build',
        depsInstall: depsInstallMeta,
      },
    };
  }

  // `fs_read` uses `sandbox.fs.*` rather than `sandbox.process.exec`, so it
  // has no ProcessResponse and no sandbox-side timestamps. We deliberately
  // skip `serverPhaseDurations` for this phase; `phaseDurations` still
  // captures the client-side wall-clock.
  const [js, css] = await timePhase(
    'fs_read',
    phaseDurations,
    serverPhaseDurations,
    async () => {
      const value = await Promise.all([
        sandbox.fs.read(`${SANDBOX_WORKING_DIR}/dist/out.mjs`),
        sandbox.fs.read(`${SANDBOX_WORKING_DIR}/dist/out.css`),
      ]);
      return {value, serverDurationMs: null};
    }
  );

  let functionsHash: string | undefined;
  if (options.codeFunctions) {
    let files: Array<{name: string}> = [];
    try {
      ({files} = await sandbox.fs.ls(`${SANDBOX_WORKING_DIR}/dist-functions`));
    } catch (error) {
      // No `functions/` files: `scripts/build-functions.ts` deletes
      // `dist-functions`. Blaxel `fs.ls` then 404s the missing path. Treat
      // that as "this library has no functions bundle", not a failed build.
      // Any other ls error still throws.
      if (!(error instanceof Error) || !error.message.includes('404')) {
        throw error;
      }
    }
    if (files.length > 1) {
      throw new Error(
        `Expected a single file in dist-functions, found ${files.length}`
      );
    }
    if (files.length === 1) {
      const name = files[0]?.name ?? '';
      const hash = /^index\.([^./]+)\.js$/.exec(name)?.[1];
      if (!hash) {
        throw new Error(
          `Expected dist-functions/index.[hash].js, found ${name}`
        );
      }
      functionsHash = hash;
    }
  }

  return {
    result: Result.Ok({
      js,
      css,
      ...(functionsHash ? {functionsHash} : {}),
    }),
    meta: {
      phaseDurations,
      serverPhaseDurations,
      phaseLogs,
      failurePhase: 'none',
      depsInstall: depsInstallMeta,
    },
  };
}

/**
 * Bundles a code library using the Webflow CLI bundler in a sandbox environment.
 *
 * This function performs the following steps:
 * 1. Installs all required dependencies including the Webflow CLI
 * 2. Scans user imports and resolves additional npm packages to install
 * 3. Executes the Webflow CLI bundler command with the specified library ID and public path
 * 4. Validates the bundling process completed successfully
 * 5. Reads and returns generated bundle files from the Client, Server, and optional Functions artifacts
 *
 * The bundler creates module federation bundles for both client-side and server-side rendering.
 *
 * @param sandbox - The sandbox instance where the bundling will be executed
 * @param codeModuleId - The unique identifier for the code module being bundled
 * @param publicPath - The public URL path where the bundled assets will be hosted (used for asset references)
 * @returns Object with success status.
 *          On success: `{success: true, files: BundleFile[]}` containing all generated bundle files.
 *          On failure: `{success: false, logs: string}` with detailed error logs from the CLI bundler
 */
export async function buildCodeLibraryWithCLI(
  sandbox: SandboxInstance,
  codeModuleId: string,
  publicPath: string,
  {
    codeFunctionsEnabled = false,
    portable = false,
  }: {codeFunctionsEnabled?: boolean; portable?: boolean} = {}
): Promise<
  | {success: true; files: BundleFile[]; portable: boolean}
  | {
      success: false;
      logs: string;
      step: PublishBundleStep;
      exitCode?: number;
      status?: string;
    }
> {
  // CLI bundler doesn't surface phase-level telemetry, so the
  // `serverDurationMs` returned by `installDependencies` is discarded here.
  const {result: installResult} = await installDependencies(sandbox);
  if (installResult.tag === 'Error') {
    return {
      success: false,
      step: 'install-dependencies',
      logs: installResult.val,
    };
  }
  // installResult.val.meta surfaces rejected / recovered names, but the CLI
  // bundler path has no telemetry sender, so the meta isn't consumed here.

  // Perform type check before bundling to use the same TS check logic
  // as buildCodeLibrary. This minimizes the chances of running into TS errors
  // during the bundling process that were not caught by the type check in development.
  const typeCheckResult = await performTypeCheck(sandbox, {
    propagateExecErrors: true,
  });
  if (!typeCheckResult.success) {
    return {
      success: false,
      step: 'type-check',
      logs: typeCheckResult.diagnostics,
    };
  }

  if (portable) {
    // Probe the installed CLI: project pins can differ on the same sandbox image.
    // Remove this compatibility probe once every supported CLI has --portable.
    const help = await sandbox.process.exec({
      command:
        'DO_NOT_TRACK=true bunx webflow library bundle --help --skip-update-check',
      workingDir: SANDBOX_WORKING_DIR,
      waitForCompletion: true,
    });
    portable =
      help.exitCode === 0 &&
      /--portable(?![\w-])/.test(logsOrProcessOutput(help.logs, help));
  }

  const options = [
    '--skip-update-check', // Skip checking for updates to the Webflow CLI
    '--no-input', // Don't prompt for input
    ...(portable ? ['--portable'] : []),
    '--bundler=rspack', // Use Rspack bundler (faster than default Webpack)
    `--library-id=${codeModuleId}`, // The library ID to bundle
    `--public-path=${portable ? publicPath.replace(/%2f/gi, '/') : publicPath}`, // The public path where the bundled assets will be hosted
    '--force', // Skip TS checks during bundling (we do our own type checking)
  ];

  const bundleResult = await sandbox.process.exec({
    command: `DO_NOT_TRACK=true bunx webflow library bundle ${options.join(' ')}`,
    workingDir: SANDBOX_WORKING_DIR,
    waitForCompletion: true,
  });

  const success =
    bundleResult.status === 'completed' && bundleResult.exitCode === 0;

  if (!success) {
    const logs = await extractLogs(sandbox, bundleResult.logs);
    return {
      success: false,
      step: 'bundle',
      logs: logsOrProcessOutput(logs, bundleResult),
      exitCode: bundleResult.exitCode,
      status: bundleResult.status,
    };
  }

  // Extract files from bundle
  const [clientFiles, serverFiles, functionsFiles] = await Promise.all([
    readBundleFiles(sandbox, CodeModuleArtifact.ComponentsBundle, {portable}),
    readBundleFiles(sandbox, CodeModuleArtifact.ComponentsSSRBundle, {
      portable,
    }),
    codeFunctionsEnabled
      ? readBundleFiles(sandbox, CodeModuleArtifact.FunctionsBundle, {
          optional: true,
          portable,
        })
      : [],
  ]);

  const files = [...clientFiles, ...serverFiles, ...functionsFiles];
  if (files.length === 0) {
    const logs = await extractLogs(sandbox, bundleResult.logs);
    return {
      success: false,
      step: 'files-extraction',
      logs: `Bundle completed but no files were generated. Logs:\n${logsOrProcessOutput(logs, bundleResult)}`,
      exitCode: bundleResult.exitCode,
      status: bundleResult.status,
    };
  }

  return {
    success: true,
    files,
    portable,
  };
}
